/**
 * src/admin-ui/src/components/edge-table/EdgeTableLayout.styles.ts
 *
 * Styled primitives of the project-wide "full-screen page" pattern
 * (see CLAUDE.md, section "Full-screen page pattern"): no page header,
 * content fills the viewport edge-to-edge, and every control lives in a
 * fixed bottom bar docked flush against the navigation footer. The
 * visual model mirrors the any-classifier edge-to-edge DataTable
 * (research/any_classifier_ui_reference.md).
 *
 * Composition cheat-sheet:
 *
 * - Table pages: use the ready-made `EdgeDataTable` component from this
 *   folder — it wires these primitives together.
 * - Non-table pages: put content into `FullBleedViewport` and controls
 *   into `BottomBar` > `BarTile` tiles. Pass `withBar={false}` to the
 *   viewport when the page has no controls (then render no bar at all).
 *
 * The bottom bar sits on `--app-footer-height` — the real footer height
 * published by Footer.tsx via ResizeObserver — so it stays flush with
 * the footer on every viewport.
 */

import { Box, Table, TableCell, TableContainer } from "@mui/material";
import { styled } from "@mui/material/styles";

/**
 * Real height of the fixed navigation footer, published from Footer.tsx.
 * The fallback matches FooterToolbar's min-height.
 */
const FOOTER_HEIGHT = "var(--app-footer-height, 60px)";

/** Height of the fixed bottom control bar (эталон any-classifier: 42px). */
export const BOTTOM_BAR_HEIGHT = 42;

/** Viewport height minus the fixed chrome below the content. */
const contentHeight = (withBar: boolean): Record<string, unknown> => {
  const chrome = withBar
    ? `${FOOTER_HEIGHT} - ${BOTTOM_BAR_HEIGHT}px`
    : FOOTER_HEIGHT;
  return {
    height: `calc(100vh - ${chrome})`,
    // Mobile browsers collapse their chrome; dvh tracks the visible
    // viewport so content never hides behind the fixed bottom bar.
    "@supports (height: 100dvh)": {
      height: `calc(100dvh - ${chrome})`,
    },
  };
};

/**
 * Generic full-bleed content area for non-table pages: fills the screen
 * from the top edge down to the bottom bar (or the footer when
 * `withBar={false}`) and scrolls internally.
 */
export const FullBleedViewport = styled(Box, {
  shouldForwardProp: (prop) => prop !== "withBar",
})<{ withBar?: boolean }>(({ theme, withBar = true }) => ({
  width: "100%",
  overflow: "auto",
  backgroundColor: theme.palette.background.paper,
  ...contentHeight(withBar),
}));

/**
 * Full-bleed table viewport: no side paddings, own scroll area so the
 * sticky table header pins to the top of the screen.
 */
export const FullBleedTableViewport = styled(TableContainer)(({ theme }) => ({
  width: "100%",
  overflow: "auto",
  backgroundColor: theme.palette.background.paper,
  ...contentHeight(true),
}));

/**
 * Edge-to-edge table: 14px body text, compact 8/16px cell paddings,
 * light row dividers and row hover — exact values of the reference
 * `.edge-list .table` / `.edge-list__row` rules. Header cells (plain
 * `TableCell` in `thead`, e.g. inside dialogs) inherit the uppercase
 * header typography; page-level tables use `HeaderCell` instead.
 */
export const EdgeTable = styled(Table)(({ theme }) => ({
  borderCollapse: "collapse",
  "& thead th": {
    whiteSpace: "nowrap",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  "& tbody td": {
    padding: theme.spacing(1, 2),
    fontSize: 14,
    borderBottom: `1px solid ${theme.palette.action.hover}`,
  },
  "& tbody tr": {
    transition: "background-color 150ms ease",
  },
  "& tbody tr:hover": {
    backgroundColor: theme.palette.background.default,
  },
}));

/**
 * Page-level header cell: zero padding so the filter trigger button (or
 * the static `HeaderStatic` label) fills the whole cell.
 */
export const HeaderCell = styled(TableCell)(({ theme }) => ({
  padding: 0,
  "&.MuiTableCell-stickyHeader": {
    backgroundColor: theme.palette.background.default,
  },
}));

/**
 * Static (non-filterable) header label sized identically to the filter
 * trigger button, so filterable and plain headers align in one row.
 */
export const HeaderStatic = styled("span", {
  shouldForwardProp: (prop) => prop !== "numeric",
})<{ numeric?: boolean }>(({ theme, numeric }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: numeric ? "flex-end" : "flex-start",
  minHeight: 32,
  padding: theme.spacing(0.5, 1),
}));

/**
 * Fixed bottom control bar in the reference `edge-list-bottom-bar`
 * style: docked flush above the navigation footer, right-packed stretch
 * tiles separated by borders on the primary background. Total counter
 * first, page-specific tiles next, pagination last.
 */
export const BottomBar = styled(Box)(({ theme }) => ({
  position: "fixed",
  left: 0,
  right: 0,
  bottom: FOOTER_HEIGHT,
  zIndex: theme.zIndex.appBar,
  minHeight: BOTTOM_BAR_HEIGHT,
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: "flex-end",
  alignItems: "stretch",
  gap: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
  [theme.breakpoints.down("md")]: {
    flexWrap: "wrap",
  },
}));

/** One bordered tile of the bottom bar (reference `__tile`). */
export const BarTile = styled(Box)(({ theme }) => ({
  minHeight: BOTTOM_BAR_HEIGHT,
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0, 2),
  borderLeft: `1px solid ${theme.palette.action.hover}`,
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    borderLeft: "none",
  },
}));

/** Leading row-count tile (reference `__total`). */
export const TotalTile = styled(BarTile)(({ theme }) => ({
  justifyContent: "flex-start",
  minWidth: 120,
  color: theme.palette.text.disabled,
  fontSize: 14,
  borderLeft: "none",
}));

/**
 * Mobile (<sm) fallback: stacked cards with side padding and bottom
 * clearance for the fixed bar + footer.
 */
export const MobileCards = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: 0,
  padding: 0,
  paddingBottom: `calc(${FOOTER_HEIGHT} + ${BOTTOM_BAR_HEIGHT}px)`,
}));

/**
 * One mobile card — the single source of the shared card look: interior
 * padding, vertical rhythm and a thin top-border separator between
 * cards (no outer margins). `EdgeDataTable` wraps every
 * `renderMobileCard` result in this component automatically, so pages
 * return card content only; change card styling here, not per page.
 */
export const MobileCard = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  "&:first-of-type": {
    borderTop: "none",
  },
}));
